/**
 * The integration screen.
 *
 * Answers the question the buyer asked directly: how does this connect to ServiceNow
 * and Jira, and which service levels does each source feed. Also states which sources
 * can back a verified claim and which cannot, so the difference is visible before
 * anyone asks.
 */

import { Notice, Section, StatTile } from "../components/Chrome";
import { ProvenanceBadge } from "../components/Badges";
import { periodLabel } from "../lib/format";
import type { SourcesResponse } from "../lib/types";

export function SourcesView({ sources }: { sources: SourcesResponse }) {
  const verifying = sources.sources.filter((source) => source.provenance !== "vendor_attested");

  return (
    <div className="fade-in">
      <Section
        title="Data sources"
        subtitle="Nothing is installed in the client environment. Each source is pulled over its own API on a schedule, normalized into one vocabulary, then measured against the contract."
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <StatTile label="Connected sources" value={String(sources.sources.length)} />
          <StatTile
            label="Verifying sources"
            value={String(verifying.length)}
            note="Can back a verified finding"
            tone="gold"
          />
          <StatTile
            label="Observations"
            value={sources.observations_total.toLocaleString()}
            note={`${periodLabel(sources.window.start)} – ${periodLabel(sources.window.end)}`}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Notice>
            The endpoints below are stand-ins that reproduce each system's real HTTP
            contract: bearer auth, cursor pagination, and the vendor's own field names.
            The connector and mapping code running against them is the code that runs
            against a live instance. Pointing at a real tenant is a base URL and a
            credential, not a rewrite.
          </Notice>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {sources.sources.map((source) => (
            <div key={source.id} className="card" style={{ padding: 18 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div>
                  <span className="serif" style={{ fontSize: 17 }}>
                    {source.label}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--slate)", marginLeft: 10 }}>
                    {source.kind}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <ProvenanceBadge provenance={source.provenance} />
                  <span style={{ fontSize: 11, color: "var(--green)" }}>● Connected</span>
                </div>
              </div>

              <div
                style={{
                  marginTop: 12,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  fontSize: 12.5,
                }}
              >
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.06em", color: "var(--slate)" }}>
                    ENDPOINT
                  </div>
                  <code style={{ color: "var(--parchment)", fontSize: 11.5, wordBreak: "break-all" }}>
                    {source.endpoint}
                  </code>
                  <div style={{ color: "var(--slate)", marginTop: 4, fontSize: 11.5 }}>
                    Auth: {source.auth}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.06em", color: "var(--slate)" }}>
                    FEEDS
                  </div>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 16, color: "var(--parchment)" }}>
                    {source.feeds.map((feed) => (
                      <li key={feed} style={{ fontSize: 12 }}>
                        {feed}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.06em", color: "var(--slate)" }}>
                    MEASUREMENT RULE
                  </div>
                  <div style={{ color: "var(--parchment)", fontSize: 12, lineHeight: 1.6 }}>
                    {source.note}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Records per vendor" subtitle="Observations normalized from the sources above.">
        <div className="card table-scroll">
          <table>
            <thead>
              <tr>
                <th>Vendor</th>
                <th style={{ textAlign: "right" }}>Observations</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(sources.observations_by_vendor).map(([vendor, count]) => (
                <tr key={vendor}>
                  <td style={{ textTransform: "capitalize" }}>{vendor}</td>
                  <td className="num" style={{ textAlign: "right" }}>
                    {count.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 12 }}>
          <Notice>
            The vendor portal is listed as a source but never verifies anything. It supplies the
            reported figure only, on a code path that shares nothing with the computation. That
            separation is what makes the two numbers on every other screen independent.
          </Notice>
        </div>
      </Section>
    </div>
  );
}
